import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';

import { isVlessClient, type SerializedClientConfig } from '../lib/api.js';

type Format = 'awg' | 'amnezia' | 'vless' | 'app';

interface FormatPayload {
  /** Plain text shown under «Показать содержимое» / encoded into the QR. */
  display: string;
  /** Mime + extension for the download blob. */
  mime: string;
  ext: string;
  label: string;
  hint: string;
}

export function formatPayloadFor(
  format: Format,
  client: SerializedClientConfig,
): FormatPayload | null {
  if (isVlessClient(client)) {
    if (format === 'app') {
      // Payloads minted by an older API carry no import link — the tab stays
      // hidden rather than showing an empty QR.
      if (!client.appImportUrl) return null;
      return {
        display: client.appImportUrl,
        mime: 'text/plain;charset=utf-8',
        ext: 'txt',
        label: 'Наше приложение',
        hint:
          'Откройте ссылку на том же устройстве — приложение подхватит профиль. ' +
          'Если кабинет открыт на другом устройстве, отсканируйте QR.',
      };
    }
    if (format !== 'vless') return null;
    return {
      display: client.vlessUrl,
      mime: 'text/plain;charset=utf-8',
      // `.txt`, not `.vless`: no client registers a handler for the latter, so
      // a custom extension only produces a file nothing will open.
      ext: 'txt',
      label: 'VLESS (ссылка)',
      hint: 'Вставьте ссылку или отсканируйте QR в любом современном клиенте: AmneziaVPN, v2rayNG, Hiddify, Streisand, NekoBox, sing-box.',
    };
  }
  if (format === 'app') {
    // Конфиги, выпущенные старым API, ссылки не несут — вкладка просто не
    // показывается, а не рисует пустой QR.
    if (!client.appImportUrl) return null;
    return {
      display: client.appImportUrl,
      mime: 'text/plain;charset=utf-8',
      ext: 'txt',
      label: 'Наше приложение',
      hint:
        'Откройте ссылку на том же устройстве — приложение подхватит профиль. ' +
        'Адрес сервера такой профиль обновляет сам; соседние ссылки — нет.',
    };
  }
  if (format === 'awg') {
    return {
      display: client.text,
      mime: 'text/plain;charset=utf-8',
      ext: 'conf',
      label: 'AmneziaWG (.conf)',
      hint: 'Импорт в нативный AmneziaWG-клиент или `awg-quick up <file>`. Подходит и для AmneziaVPN-app: «Импортировать конфиг WireGuard».',
    };
  }
  if (format !== 'amnezia' || !client.vpnUrl) return null;
  return {
    display: client.vpnUrl,
    mime: 'text/plain;charset=utf-8',
    ext: 'vpn',
    label: 'AmneziaVPN (vpn://)',
    hint: 'Импорт одним тапом в коробочный AmneziaVPN: открыть приложение → «Добавить сервер» → вставить ссылку или отсканировать QR.',
  };
}

export function slugify(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 32) || 'config'
  );
}

interface Props {
  client: SerializedClientConfig;
  /** Used in the download filename and as the AmneziaVPN-app server label. */
  configName: string;
}

const PRIMARY_BTN =
  'rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-dark';
const SECONDARY_BTN =
  'rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-brand hover:text-brand';
const SECTION_LABEL = 'text-[10px] font-semibold uppercase tracking-wide text-slate-500';

type ActionId = 'copy-link' | 'copy-file' | 'share-link' | 'share-file';

/**
 * Renders the format tabs + QR + actions for a single freshly-minted peer.
 * Shared between the single-config detail page and the multi-device batch
 * reveal block on the dashboard.
 *
 * Both formats (AmneziaVPN `.vpn` / vpn:// and AmneziaWG `.conf`) expose two
 * action groups so the user can pick the workflow that fits the target app:
 *   - «Ссылка»: open in app (vpn:// only) + copy the text + share the text
 *   - «Файл»:   download with the right extension + open in a new tab + share
 *               the actual file via the system share sheet
 *
 * The `vpn://` open button is a plain anchor — `window.location.href` triggers
 * Safari's strict URL bar parser which rejects the scheme as «адрес
 * недействителен» on devices without AmneziaVPN installed. Anchor clicks go
 * through the lenient app-handoff path: when AmneziaVPN is installed the OS
 * launches it; when it isn't, the new-tab fallback keeps the cabinet visible.
 */
export function ClientConfigPanel({ client, configName }: Props) {
  const isVless = isVlessClient(client);
  const [format, setFormat] = useState<Format>(() => {
    if (isVless) return 'vless';
    return client.vpnUrl != null ? 'amnezia' : 'awg';
  });
  const [qr, setQr] = useState<string | null>(null);
  const [flash, setFlash] = useState<ActionId | null>(null);
  const [copyError, setCopyError] = useState<ActionId | null>(null);

  const payload = useMemo(() => formatPayloadFor(format, client), [format, client]);
  // VLESS servers offer exactly one format, so its own payload is the fallback.
  const fallbackPayload = useMemo(
    () => formatPayloadFor(isVless ? 'vless' : 'awg', client)!,
    [client, isVless],
  );
  const active = payload ?? fallbackPayload;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const url = await QRCode.toDataURL(active.display, {
          margin: 1,
          width: 320,
          color: { dark: '#0f172a', light: '#f8fafc' },
        });
        if (!cancelled) setQr(url);
      } catch {
        if (!cancelled) setQr(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active.display]);

  useEffect(() => {
    setFlash(null);
    setCopyError(null);
  }, [format]);

  const fileName = `${slugify(configName)}.${active.ext}`;

  const blobUrl = useMemo(() => {
    const blob = new Blob([active.display], { type: active.mime });
    return URL.createObjectURL(blob);
  }, [active.display, active.mime]);

  useEffect(() => {
    return () => URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  // Build a File once per format so the share-file button can advertise it
  // through navigator.canShare({ files }) before showing itself.
  const sharedFile = useMemo(() => {
    try {
      return new File([active.display], fileName, { type: active.mime });
    } catch {
      return null;
    }
  }, [active.display, active.mime, fileName]);

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const canShareFile =
    canShare &&
    typeof navigator.canShare === 'function' &&
    sharedFile != null &&
    safeCanShare({ files: [sharedFile] });

  // The App-Store build of AmneziaVPN for iOS registers neither the `vpn://`
  // URL scheme nor a UTI for `.vpn` files — both deep-link paths just hit
  // Safari's «адрес недействителен» error or land in Files with no handler.
  // The only working import on iOS is in-app: «Добавить сервер» → scan QR
  // or paste the link. Detect iOS so we can suppress the dead «Open» button
  // and surface a hint that matches what the user actually has to do.
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS 13+ reports as Macintosh with multi-touch.
    return /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  }, []);

  const flashFor = (id: ActionId) => {
    setFlash(id);
    setCopyError(null);
    window.setTimeout(() => {
      setFlash((current) => (current === id ? null : current));
    }, 2_000);
  };

  const reportCopyError = (id: ActionId) => {
    setCopyError(id);
    window.setTimeout(() => {
      setCopyError((current) => (current === id ? null : current));
    }, 2_000);
  };

  const onCopy = async (id: ActionId) => {
    try {
      await navigator.clipboard.writeText(active.display);
      flashFor(id);
    } catch {
      reportCopyError(id);
    }
  };

  const onOpenFile = () => {
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
  };

  const onShareLink = async () => {
    if (!deepLink) return;
    try {
      // Send the URL as `text`, not `url`: Safari rejects non-https values in
      // the `url` field, while `text` is passed verbatim to the share sheet
      // (Telegram, Notes, Mail, etc.) so the recipient gets the tappable
      // vpn:// / vless:// string.
      await navigator.share({ title: configName, text: deepLink });
      flashFor('share-link');
    } catch {
      // User cancelled or share failed — no toast, no fuss.
    }
  };

  const onShareText = async () => {
    try {
      await navigator.share({ title: fileName, text: active.display });
      flashFor('share-link');
    } catch {
      // Swallow.
    }
  };

  const onShareFile = async () => {
    if (!sharedFile) return;
    try {
      await navigator.share({ title: fileName, files: [sharedFile] });
      flashFor('share-file');
    } catch {
      // Swallow.
    }
  };

  const amneziaAvailable = !isVless && client.vpnUrl != null;
  const isVpn = format === 'amnezia' && amneziaAvailable;
  const appImportUrl = client.appImportUrl;
  const isApp = format === 'app' && appImportUrl != null;
  /**
   * The custom-scheme URL for the active format, if it has one. `vpn://`,
   * `vless://` and our own `nevodapp://` are handed to the OS the same way;
   * only the `.conf` format has no link to open or share.
   */
  const deepLink = isApp
    ? appImportUrl
    : isVless
      ? client.vlessUrl
      : isVpn
        ? client.vpnUrl
        : undefined;

  const copyLinkLabel =
    copyError === 'copy-link'
      ? 'Не удалось'
      : flash === 'copy-link'
        ? 'Скопировано'
        : 'Скопировать';
  const copyFileLabel =
    copyError === 'copy-file'
      ? 'Не удалось'
      : flash === 'copy-file'
        ? 'Скопировано'
        : 'Скопировать содержимое';
  const shareLinkLabel = flash === 'share-link' ? 'Отправлено' : 'Поделиться';
  const shareFileLabel = flash === 'share-file' ? 'Отправлено' : 'Поделиться файлом';

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {isVless ? (
          // The VLESS link is the universally understood format; our own
          // `nevodapp://` link shows up next to it only when the API minted
          // one. With no import link the row is a single informational tab.
          <>
            <FormatTab
              active={format === 'vless'}
              onClick={() => setFormat('vless')}
              label="VLESS (ссылка)"
              recommended
            />
            {appImportUrl != null && (
              <FormatTab
                active={format === 'app'}
                onClick={() => setFormat('app')}
                label="Наше приложение"
              />
            )}
          </>
        ) : (
          <>
            <FormatTab
              active={format === 'amnezia'}
              onClick={() => setFormat('amnezia')}
              label="AmneziaVPN (vpn://)"
              recommended={amneziaAvailable}
              disabled={!amneziaAvailable}
              disabledTitle={
                amneziaAvailable
                  ? undefined
                  : 'Конфиг был выпущен на старой версии API без поддержки vpn-ссылки. Перевыпустите конфиг, чтобы получить оба формата.'
              }
            />
            <FormatTab
              active={format === 'awg'}
              onClick={() => setFormat('awg')}
              label="AmneziaWG (.conf)"
            />
            {appImportUrl != null && (
              <FormatTab
                active={format === 'app'}
                onClick={() => setFormat('app')}
                label="Наше приложение"
              />
            )}
          </>
        )}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">{active.hint}</p>
      {isIOS && isVless && format === 'vless' && (
        <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
          На iOS откройте приложение и добавьте сервер вручную: «Поделиться» → выберите клиент, либо
          скопируйте ссылку и вставьте её в приложении, либо отсканируйте QR.
        </p>
      )}
      {isIOS && isVpn && (
        <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
          На iOS AmneziaVPN не открывает <code className="font-mono">vpn://</code>-ссылки и
          .vpn-файлы напрямую. Нажмите «Поделиться» и выберите AmneziaVPN в системном меню — либо
          отсканируйте QR справа, либо скопируйте ссылку и вставьте её в приложении.
        </p>
      )}

      <div className="mt-4 flex flex-col items-center gap-5 sm:flex-row sm:items-start">
        {qr ? (
          <img
            src={qr}
            alt={`QR-код конфига (${active.label})`}
            width={220}
            height={220}
            className="rounded-lg border border-amber-500/30 bg-white p-2"
          />
        ) : (
          <div className="h-[220px] w-[220px] animate-pulse rounded-lg bg-slate-900" />
        )}

        <div className="flex-1 space-y-4">
          <section>
            <p className={SECTION_LABEL}>Ссылка</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {/* Our own client registers `nevodapp://` on iOS too, so the
                  one-tap anchor survives there — unlike vpn:// and vless://. */}
              {deepLink && (!isIOS || isApp) && (
                <a
                  href={deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={PRIMARY_BTN}
                >
                  {isApp || isVless ? 'Открыть в приложении' : 'Открыть в AmneziaVPN'}
                </a>
              )}
              {canShare && (
                <button
                  onClick={deepLink ? onShareLink : onShareText}
                  className={deepLink && isIOS && !isApp ? PRIMARY_BTN : SECONDARY_BTN}
                >
                  {shareLinkLabel}
                </button>
              )}
              <button onClick={() => onCopy('copy-link')} className={SECONDARY_BTN}>
                {copyLinkLabel}
              </button>
            </div>
          </section>

          <section>
            <p className={SECTION_LABEL}>Файл</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <a
                href={blobUrl}
                download={fileName}
                className={deepLink ? SECONDARY_BTN : PRIMARY_BTN}
              >
                Скачать .{active.ext}
              </a>
              <button onClick={() => onCopy('copy-file')} className={SECONDARY_BTN}>
                {copyFileLabel}
              </button>
              <button onClick={onOpenFile} className={SECONDARY_BTN}>
                Открыть
              </button>
              {canShareFile && (
                <button onClick={onShareFile} className={SECONDARY_BTN}>
                  {shareFileLabel}
                </button>
              )}
            </div>
          </section>

          <details className="rounded-lg border border-slate-800 bg-slate-950 p-3">
            <summary className="cursor-pointer select-none text-xs text-slate-400">
              Показать содержимое
            </summary>
            <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-snug text-slate-300">
              {active.display}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

function safeCanShare(data: ShareData): boolean {
  try {
    return navigator.canShare(data);
  } catch {
    return false;
  }
}

function FormatTab({
  active,
  onClick,
  label,
  disabled,
  disabledTitle,
  recommended,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  disabledTitle?: string;
  recommended?: boolean;
}) {
  const base =
    'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  const cls = active
    ? `${base} bg-brand text-white`
    : `${base} border border-slate-700 text-slate-200 hover:border-brand hover:text-brand`;
  return (
    <button onClick={onClick} className={cls} disabled={disabled} title={disabledTitle}>
      <span>{label}</span>
      {recommended && (
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            active ? 'bg-white/20 text-white' : 'bg-emerald-500/15 text-emerald-300'
          }`}
        >
          Рекомендуем
        </span>
      )}
    </button>
  );
}
